// src/components/fulfillment/BuyLabelDialog.tsx
import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Stack,
  Alert,
  CircularProgress,
  Typography,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import {
  buyShippingLabel,
  getShippingRates,
  type ShippingRate,
} from "../../api/fulfillmentApi";

type Props = {
  open: boolean;
  loading?: boolean;
  onClose: () => void;
  onPurchased?: (res: {
    carrier?: string;
    tracking_number?: string;
    tracking_url?: string;
    label_url?: string;
  }) => void;
  fulfillmentId: number;
  orderId: number;
  hasExistingLabel?: boolean;
};

type PackageForm = {
  weightOz: string;
  lengthIn: string;
  widthIn: string;
  heightIn: string;
};

const DEFAULT_PACKAGE: PackageForm = {
  weightOz: "16",
  lengthIn: "8",
  widthIn: "6",
  heightIn: "2",
};

function toPositiveNumber(value: string, fallback: number) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

function buildPackagePayload(pkg: PackageForm) {
  return {
    weightOz: toPositiveNumber(pkg.weightOz, 16),
    lengthIn: toPositiveNumber(pkg.lengthIn, 8),
    widthIn: toPositiveNumber(pkg.widthIn, 6),
    heightIn: toPositiveNumber(pkg.heightIn, 2),
  };
}

export default function BuyLabelDialog({
  open,
  loading,
  onClose,
  onPurchased,
  fulfillmentId,
  orderId,
  hasExistingLabel = false,
}: Props) {
  const [pkg, setPkg] = useState<PackageForm>(DEFAULT_PACKAGE);

  const [rates, setRates] = useState<ShippingRate[]>([]);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [ratesError, setRatesError] = useState<string | null>(null);
  const [serviceCode, setServiceCode] = useState<string>("");

  const [labelLoading, setLabelLoading] = useState(false);
  const [labelError, setLabelError] = useState<string | null>(null);
  const [labelOk, setLabelOk] = useState<string | null>(null);
  const [labelUrl, setLabelUrl] = useState<string>("");

  const alreadyPurchased = hasExistingLabel;
  const hasBought = !!labelUrl;

  const handlePkgChange =
    (field: keyof PackageForm) =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;

        // allow empty string so user can clear field
        // allow digits and decimal point only
        if (!/^\d*\.?\d*$/.test(value)) return;

        setPkg((prev) => ({
          ...prev,
          [field]: value,
        }));
      };

  const validatePackage = () => {
    const parsed = buildPackagePayload(pkg);

    if (
      parsed.weightOz <= 0 ||
      parsed.lengthIn <= 0 ||
      parsed.widthIn <= 0 ||
      parsed.heightIn <= 0
    ) {
      return "All package dimensions and weight must be greater than 0.";
    }

    return null;
  };

  const fetchRates = async (opts?: { resetService?: boolean }) => {
    setRatesError(null);

    const validationError = validatePackage();
    if (validationError) {
      setRates([]);
      setServiceCode("");
      setRatesError(validationError);
      return;
    }

    setRatesLoading(true);

    try {
      const packagePayload = buildPackagePayload(pkg);

      const data = await getShippingRates({
        orderId,
        package: packagePayload,
      });

      const list = Array.isArray(data.rates) ? data.rates : [];
      // Sort cheapest → most expensive so the list is predictable and the
      // default selection is the lowest price.
      const sorted = [...list].sort(
        (a, b) =>
          Number(a.total_amount?.amount || 0) - Number(b.total_amount?.amount || 0)
      );
      setRates(sorted);

      if (!sorted.length) {
        setServiceCode("");
        setRatesError("No services available for this destination/package.");
        return;
      }

      const stillValid = sorted.some((r) => r.service_code === serviceCode);
      if (opts?.resetService || !serviceCode || !stillValid) {
        setServiceCode(sorted[0].service_code);
      }
    } catch (e: any) {
      console.error(e);

      const apiErr = e?.response?.data;
      const shippoMsg =
        apiErr?.shippo?.messages?.[0]?.text ||
        apiErr?.shippo?.messages?.[0]?.message ||
        null;

      setRates([]);
      setServiceCode("");
      setRatesError(
        shippoMsg
          ? `${apiErr?.error || "Failed to fetch rates"}`
          : apiErr?.error || e?.message || "Failed to fetch rates"
      );
    } finally {
      setRatesLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;

    setLabelError(null);
    setLabelOk(null);
    setLabelUrl("");
    setLabelLoading(false);

    setPkg(DEFAULT_PACKAGE);

    setRates([]);
    setRatesError(null);
    setServiceCode("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, orderId]);

  // Auto-fetch (debounced) on open and whenever the package inputs change, so
  // the user doesn't have to click "Refresh Rates" after editing weight/size.
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      fetchRates();
    }, 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, orderId, pkg.weightOz, pkg.lengthIn, pkg.widthIn, pkg.heightIn]);

  const handleBuyLabel = async () => {
    setLabelError(null);
    setLabelOk(null);

    const validationError = validatePackage();
    if (validationError) {
      setLabelError(validationError);
      return;
    }

    setLabelLoading(true);

    try {
      const packagePayload = buildPackagePayload(pkg);

      const res = await buyShippingLabel({
        fulfillmentId,
        orderId,
        service_code: serviceCode,
        package: packagePayload,
      });

      const nextLabelUrl = res.label_url || "";
      setLabelUrl(nextLabelUrl);

      setLabelOk("✅ Label purchased successfully! Tracking and label saved.");

      onPurchased?.({
        carrier: res.carrier,
        tracking_number: res.tracking_number,
        tracking_url: res.tracking_url,
        label_url: res.label_url,
      });
    } catch (e: any) {
      console.error(e);
      const apiErr = e?.response?.data;
      const shippoMsg =
        apiErr?.shippo?.messages?.[0]?.text ||
        apiErr?.shippo?.messages?.[0]?.message ||
        null;

      setLabelError(
        shippoMsg
          ? `${apiErr?.error || "Failed to buy label"} — ${shippoMsg}`
          : apiErr?.error || e?.message || "Failed to buy label"
      );
    } finally {
      setLabelLoading(false);
    }
  };

  const canBuyLabel =
    !alreadyPurchased &&
    !hasBought &&
    !labelLoading &&
    !loading &&
    !!serviceCode &&
    !!rates.length &&
    !ratesLoading;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Buy Shipping Label (Shippo)</DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          This will purchase a label, save tracking to the fulfillment, and store
          the PDF link for printing.
        </Typography>

        {alreadyPurchased && (
          <Alert severity="info" sx={{ mt: 2 }}>
            A label has already been purchased for this fulfillment. Close this
            dialog and proceed to print the label.
          </Alert>
        )}

        {labelError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {labelError}
          </Alert>
        )}

        {labelOk && (
          <Alert
            severity="success"
            sx={{ mt: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  setLabelOk(null);
                  onClose();
                }}
              >
                Close
              </Button>
            }
          >
            {labelOk}
          </Alert>
        )}

        <Stack spacing={2} sx={{ mt: 2 }}>
          <TextField
            select
            fullWidth
            label="Service (Shippo)"
            value={serviceCode}
            onChange={(e) => setServiceCode(e.target.value)}
            helperText={
              ratesLoading
                ? "Loading available services..."
                : ratesError
                  ? ratesError
                  : alreadyPurchased
                    ? "Cannot purchase - label already exists"
                    : "Choose a service valid for this destination."
            }
            disabled={ratesLoading || !rates.length || hasBought || alreadyPurchased}
          >
            {rates.map((r) => (
              <MenuItem key={r.service_code} value={r.service_code}>
                {`${r.carrier_code || ""} ${r.service_type || "Service"}`.trim()}
                {r.total_amount
                  ? ` — ${r.total_amount.amount} ${r.total_amount.currency}`
                  : ""}
                {typeof r.delivery_days === "number"
                  ? ` — ${r.delivery_days}d`
                  : ""}
              </MenuItem>
            ))}
          </TextField>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              fullWidth
              label="Weight (oz)"
              type="text"
              value={pkg.weightOz}
              onChange={handlePkgChange("weightOz")}
              onFocus={(e) => e.target.select()}
              inputProps={{ inputMode: "decimal" }}
              disabled={hasBought || alreadyPurchased}
            />
            <TextField
              fullWidth
              label="Length (in)"
              type="text"
              value={pkg.lengthIn}
              onChange={handlePkgChange("lengthIn")}
              onFocus={(e) => e.target.select()}
              inputProps={{ inputMode: "decimal" }}
              disabled={hasBought || alreadyPurchased}
            />
            <TextField
              fullWidth
              label="Width (in)"
              type="text"
              value={pkg.widthIn}
              onChange={handlePkgChange("widthIn")}
              onFocus={(e) => e.target.select()}
              inputProps={{ inputMode: "decimal" }}
              disabled={hasBought || alreadyPurchased}
            />
            <TextField
              fullWidth
              label="Height (in)"
              type="text"
              value={pkg.heightIn}
              onChange={handlePkgChange("heightIn")}
              onFocus={(e) => e.target.select()}
              inputProps={{ inputMode: "decimal" }}
              disabled={hasBought || alreadyPurchased}
            />
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Button
              variant="outlined"
              onClick={() => fetchRates({ resetService: true })}
              disabled={
                ratesLoading || labelLoading || !!loading || hasBought || alreadyPurchased
              }
              sx={{ borderRadius: 2, textTransform: "none", flex: 1 }}
            >
              {ratesLoading ? "Refreshing…" : "Refresh Rates"}
            </Button>

            <Button
              variant="contained"
              onClick={handleBuyLabel}
              disabled={!canBuyLabel}
              startIcon={labelLoading ? <CircularProgress size={16} /> : undefined}
              sx={{ borderRadius: 2, textTransform: "none", flex: 1 }}
            >
              {alreadyPurchased ? "Label Already Purchased" : "Buy Label"}
            </Button>
          </Stack>

          {!!labelUrl && (
            <Button
              component="a"
              href={labelUrl}
              target="_blank"
              rel="noreferrer"
              variant="contained"
              color="success"
              fullWidth
              startIcon={<PrintIcon />}
              sx={{ borderRadius: 2, textTransform: "none", mt: 2 }}
            >
              Print Label Now (PDF)
            </Button>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined" disabled={!!loading || labelLoading}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}